'use client'

import React, { useState } from 'react'

import { Box, Text } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import { CardExpandable } from '../Card'
import {
  checkboxGlyphStyles,
  checkboxStyles,
  chevronStyles,
  rowDescriptionStyles,
  rowDetailStyles,
  rowLabelStyles,
  rowLinkStyles,
  rowMetadataWrapperStyles,
  rowResultStyles,
  rowRootStyles,
  rowTriggerStyles,
} from './ReasoningTrail.styles'
import { ReasoningTrailMetadataCard } from './ReasoningTrailMetadataCard'
import {
  REASONING_TRAIL_ARIA,
  REASONING_TRAIL_COPY,
  RESULT_LABEL,
} from './constants'
import { CheckGlyph, ChevronRightGlyph, XGlyph } from './icons'
import type { ReasoningTrailStep } from './types'

type ReasoningTrailRowProps = {
  step: ReasoningTrailStep
  /** True only for the decisive step of a resolved trail (green emphasis). */
  emphasized: boolean
  colorScheme: ColorScheme
  onViewAccessLogs?: (step: ReasoningTrailStep) => void
  /** Invoked with the new expanded state whenever this row is toggled. */
  onToggle?: (step: ReasoningTrailStep, isExpanded: boolean) => void
}

export const ReasoningTrailRow = ({
  step,
  emphasized,
  colorScheme,
  onViewAccessLogs,
  onToggle,
}: ReasoningTrailRowProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  // Compute the next state and fire the side effect from the event handler, not
  // inside the state updater — React may invoke updaters more than once, which
  // would emit duplicate onToggle (and thus analytics) events.
  const toggle = () => {
    const isExpandedNext = !isExpanded
    setIsExpanded(isExpandedNext)
    onToggle?.(step, isExpandedNext)
  }

  return (
    <Box {...rowRootStyles}>
      <Box
        {...rowTriggerStyles}
        aria-expanded={isExpanded}
        aria-label={REASONING_TRAIL_ARIA.expandRow(step.label)}
        onClick={toggle}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            toggle()
          }
        }}
        role='button'
        tabIndex={0}
      >
        <Box {...checkboxStyles(colorScheme, emphasized)}>
          <Box {...checkboxGlyphStyles}>
            {step.result === 'YES' && <CheckGlyph />}
            {step.result === 'NO' && <XGlyph />}
          </Box>
        </Box>
        <Text {...rowLabelStyles(colorScheme, emphasized)}>{step.label}</Text>
        <Text {...rowResultStyles(colorScheme, emphasized)}>
          {RESULT_LABEL[step.result]}
        </Text>
        <Box {...chevronStyles(colorScheme, isExpanded)}>
          <ChevronRightGlyph />
        </Box>
      </Box>

      <CardExpandable isExpanded={isExpanded}>
        <Box {...rowDetailStyles}>
          <Text {...rowDescriptionStyles(colorScheme, emphasized)}>
            {step.description}
          </Text>

          {step.metadataCard && (
            <Box {...rowMetadataWrapperStyles}>
              <ReasoningTrailMetadataCard
                colorScheme={colorScheme}
                metadataCard={step.metadataCard}
              />
            </Box>
          )}

          {step.hasAccessLogs && (
            <Text
              {...rowLinkStyles(colorScheme)}
              onClick={() => onViewAccessLogs?.(step)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  onViewAccessLogs?.(step)
                }
              }}
              role='button'
              tabIndex={0}
            >
              {REASONING_TRAIL_COPY.VIEW_ACCESS_LOGS}
            </Text>
          )}
        </Box>
      </CardExpandable>
    </Box>
  )
}
